import axios from 'axios'
import { getAccessToken, setAccessToken, clearAccessToken } from '../utils/tokenManager'
import store from '../store/configureStore'
import { AUTH, LOGOUT } from '../actions/constants'

// const API = axios.create({ baseURL: 'http://localhost:5000'})
const API = axios.create({ baseURL: process.env.REACT_APP_API, withCredentials: true })
const refreshClient = axios.create({ baseURL: process.env.REACT_APP_API, withCredentials: true })

API.interceptors.request.use((req) => {
    const token = getAccessToken()
    if(token) {
        req.headers.authorization = `Bearer ${token}`
    } else {
        delete req.headers.authorization
    }
    return req
})

let isRefreshing = false
let refreshQueue = []

const processRefreshQueue = (error, token) => {
    refreshQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error)
        } else {
            resolve(token)
        }
    })
    refreshQueue = []
}

API.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { response, config } = error
        const originalRequest = config || {}
        const status = response?.status
        const isAuthEndpoint =
            originalRequest?.url?.includes('/users/refresh') ||
            originalRequest?.url?.includes('/users/logout')

        if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    refreshQueue.push({
                        resolve: (token) => {
                            if (token) {
                                originalRequest.headers.authorization = `Bearer ${token}`
                            } else {
                                delete originalRequest.headers.authorization
                            }
                            resolve(API(originalRequest))
                        },
                        reject: (err) => reject(err),
                    })
                })
            }

            originalRequest._retry = true
            isRefreshing = true

            try {
                const { data } = await refreshClient.post('/users/refresh')
                if (data?.token) {
                    setAccessToken(data.token, data.expiresIn)
                    store.dispatch({ type: AUTH, data })
                    originalRequest.headers.authorization = `Bearer ${data.token}`
                } else {
                    delete originalRequest.headers.authorization
                }
                processRefreshQueue(null, data?.token)
                return API(originalRequest)
            } catch (refreshErr) {
                clearAccessToken()
                store.dispatch({ type: LOGOUT })
                processRefreshQueue(refreshErr, null)
                return Promise.reject(refreshErr)
            } finally {
                isRefreshing = false
            }
        }

        return Promise.reject(error)
    }
)

// export const fetchInvoices =() => API.get('/invoices')
export const fetchInvoice =(id) => API.get(`/invoices/${id}`)
export const addInvoice =( invoice ) => API.post('/invoices', invoice)
export const updateInvoice = (id, updatedInvoice) => API.patch(`/invoices/${id}`, updatedInvoice)
export const deleteInvoice =(id) => API.delete(`/invoices/${id}`)
export const fetchInvoicesByUser = (searchQuery) => API.get(`/invoices?searchQuery=${searchQuery.search}`);
export const fetchInvoicesByDueRange = ({ from, to } = {}) => {
  const params = new URLSearchParams();
  if (from) params.append('from', from);
  if (to) params.append('to', to);
  const qs = params.toString();
  const suffix = qs ? `?${qs}` : '';
  return API.get(`/invoices/due-range${suffix}`);
};
export const fetchDeletedInvoices = () => API.get(`/invoices/deleted`);
export const restoreInvoice = (id) => API.patch(`/invoices/${id}/restore`);
export const deleteInvoicePermanent = (id) => API.delete(`/invoices/${id}/permanent`);
export const fetchInvoiceLite = (id) => API.get(`/invoices/${id}`);
export const fetchActivityLogs = ({ page = 0, limit = 25, entityId, entityType } = {}) => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  })
  if (entityId) params.append('entityId', entityId)
  if (entityType) params.append('entityType', entityType)
  return API.get(`/activity-logs?${params.toString()}`)
}

export const fetchWhite =(id) => API.get(`/whites/${id}`)
export const addWhite =( white ) => API.post('/whites', white)
export const updateWhite = (id, updatedWhite) => API.patch(`/whites/${id}`, updatedWhite)
export const deleteWhite =(id) => API.delete(`/whites/${id}`)
export const fetchWhitesByUser = (searchQuery) => API.get(`/whites?searchQuery=${searchQuery.search}`);

export const fetchClient = (id) => API.get(`/clients/${id}`);
export const fetchClients = (page) => API.get(`/clients?page=${page}`);
export const addClient =( client ) => API.post('/clients', client)
export const updateClient = (id, updatedClient) => API.patch(`/clients/${id}`, updatedClient)
export const deleteClient =(id) => API.delete(`/clients/${id}`)
export const fetchClientsByUser = (searchQuery) => API.get(`/clients/user?searchQuery=${searchQuery.search}`);

export const fetchProduct = (id) => API.get(`/products/${id}`);
export const fetchProducts = (page) => API.get(`/products?page=${page}`);
export const addProduct =( product ) => API.post('/products', product)
export const updateProduct = (id, updatedProduct) => API.patch(`/products/${id}`, updatedProduct)
export const deleteProduct =(id) => API.delete(`/products/${id}`)
export const fetchProductsByUser = (searchQuery) => API.get(`/products/user?searchQuery=${searchQuery.search}`);

export const fetchRet = (date) => API.get(`/rets/${date}`);
export const fetchRets = (page) => API.get(`/rets?page=${page}`);
export const addRet =( ret ) => API.post('/rets', ret)
export const updateRet = (date, updatedRet) => API.patch(`/rets/${date}`, updatedRet)
export const deleteRet =(id) => API.delete(`/rets/${id}`)
export const fetchRetsByUser = (searchQuery) => API.get(`/rets/user?searchQuery=${searchQuery.search}`);

export const fetchLockup = (id) => API.get(`/lockups/${id}`);
export const fetchLockups = (page) => API.get(`/lockups?page=${page}`);
export const addLockup =( lockup ) => API.post('/lockups', lockup)
export const updateLockup = (id, updatedLockup) => API.patch(`/lockups/${id}`, updatedLockup)
export const deleteLockup =(id) => API.delete(`/lockups/${id}`)
export const fetchLockupsByUser = (searchQuery) => API.get(`/lockups/user?searchQuery=${searchQuery.search}`);

export const fetchInventory = (id) => API.get(`/inventory/${id}`);
export const fetchInventories = (page) => API.get(`/inventory?page=${page}`);
export const addInventory =( inventory ) => API.post('/inventory', inventory)
export const updateInventory = (id, updatedInventory) => API.patch(`/inventory/${id}`, updatedInventory)
export const deleteInventory =(id) => API.delete(`/inventory/${id}`)
export const fetchInventoriesByUser = (searchQuery) => API.get(`/inventory/user?searchQuery=${searchQuery.search}`);

export const fetchRoute = (id) => API.get(`/routes/${id}`);
export const fetchRoutes = (page) => API.get(`/routes?page=${page}`);
export const addRoute =( client ) => API.post('/routes', client)
export const updateRoute = (id, updatedRoute) => API.patch(`/routes/${id}`, updatedRoute)
export const deleteRoute =(id) => API.delete(`/routes/${id}`)
export const fetchRoutesByUser = (searchQuery) => API.get(`/routes/user?searchQuery=${searchQuery.search}`);

export const fetchRoutePlans = ({ from, to, status } = {}) => {
  const params = new URLSearchParams()
  if (from) params.append('from', from)
  if (to) params.append('to', to)
  if (status?.length) params.append('status', status.join(','))
  const qs = params.toString()
  const suffix = qs ? `?${qs}` : ''
  return API.get(`/route-plans${suffix}`)
}
export const fetchRoutePlan = (id) => API.get(`/route-plans/${id}`)
export const fetchRoutePlanCandidates = ({ from, to, statusIn, limit } = {}) => {
  const params = new URLSearchParams()
  if (from) params.append('from', from)
  if (to) params.append('to', to)
  if (statusIn?.length) params.append('statusIn', statusIn.join(','))
  if (limit) params.append('limit', String(limit))
  const qs = params.toString()
  const suffix = qs ? `?${qs}` : ''
  return API.get(`/route-plans/candidates${suffix}`)
}
export const createRoutePlan = (payload) => API.post('/route-plans', payload)
export const updateRoutePlan = (id, payload) => API.patch(`/route-plans/${id}`, payload)
export const deleteRoutePlan = (id) => API.delete(`/route-plans/${id}`)

export const fetchTaxRate = ({ state, zip, taxRegionName } = {}) => {
  const params = new URLSearchParams()
  if (state) params.append('state', state)
  if (zip) params.append('zip', zip)
  if (taxRegionName) params.append('taxRegionName', taxRegionName)
  const qs = params.toString()
  return API.get(`/tax-rates${qs ? `?${qs}` : ''}`)
}

export const fetchAccountsByUser = (searchQuery) => API.get(`/accounts/user?searchQuery=${searchQuery.search}`)
export const addAccount = (account) => API.post('/accounts', account)
export const updateAccount = (id, account) => API.patch(`/accounts/${id}`, account)
export const deleteAccount = (id) => API.delete(`/accounts/${id}`)

export const fetchLocationsByUser = (searchQuery) => API.get(`/locations/user?searchQuery=${searchQuery.search}`)
export const addLocation = (location) => API.post('/locations', location)
export const updateLocation = (id, location) => API.patch(`/locations/${id}`, location)
export const deleteLocation = (id) => API.delete(`/locations/${id}`)

export const fetchContactsByUser = (searchQuery) => API.get(`/contacts/user?searchQuery=${searchQuery.search}`)
export const addContact = (contact) => API.post('/contacts', contact)
export const updateContact = (id, contact) => API.patch(`/contacts/${id}`, contact)
export const deleteContact = (id) => API.delete(`/contacts/${id}`)

export const fetchOrdersByUser = (searchQuery) => API.get(`/orders/user?searchQuery=${searchQuery?.search || ''}`)
export const fetchOrder = (id) => API.get(`/orders/${id}`)
export const upsertOrderFromInvoice = (invoice) => API.post('/orders/sync', invoice)
export const updateOrder = (id, order) => API.patch(`/orders/${id}`, order)

export const signIn =(formData)=> API.post('/users/signin', formData)
export const signUp =(formData)=> API.post('/users/signup', formData)
export const forgot = (formData) => API.post('/users/forgot', formData);
export const reset = (formData) => API.post('/users/reset', formData);
export const refreshToken = () => API.post('/users/refresh')
export const logout = () => API.post('/users/logout')

export const fetchProfilesBySearch = (searchQuery) => API.get(`/profiles/search?searchQuery=${searchQuery.search || searchQuery.year || 'none'}`);
export const fetchProfile = (id) => API.get(`/profiles/${id}`)
export const fetchProfiles = () => API.get('/profiles');
export const fetchProfilesByUser = (searchQuery) => API.get(`/profiles?searchQuery=${searchQuery.search}`)
export const createProfile = (newProfile) => API.post('/profiles', newProfile);
export const updateProfile = (id, updatedProfile) => API.patch(`/profiles/${id}`, updatedProfile);
export const deleteProfile = (id) => API.delete(`/profiles/${id}`);

export const fetchOrderSheet = (id) => API.get(`/order-sheet/${id}`)

export const sendPdf = () => API.post(`/pdfs/send-pdf`)
